import { ContextualInfoButton, ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { DownloadButton } from "@/components/buttons/DownloadButton";
import { getTheme, isTpuLabellingEnabled } from "@/features/branding";
import { AnalysisResultsTab, HubMapPointsDisplay } from "@/features/maps";
import { getReturnPeriodLabel } from "@/features/pe_aldd";
import { logToolUsage } from "@/features/tracking";
import { store } from "@/store";
import { GetTpuSubunitRequest, supabaseApi, useLazyGetTpuLabellingQuery } from "@/store/services/supabase";
import styles from "@/styles/pages/quick/batch.module.scss";
import { UnmatchedType } from "@/tools/risk-bands/batch";
import { GeocodeWithResult, GeocodeWithSingleRcp, MultipleResultsTable } from "@/tools/risk-bands/components/MultipleResultsTable";
import ScreeningResultsModal, { ScreeningResultModalRefs } from "@/tools/risk-bands/components/ScreeningResultsModal";
import { UnmatchedAddressesCollapse } from "@/tools/risk-bands/components/UnmatchedAddressesCollapse";
import { RiskBandsAdvancedSettingsValues } from "@/tools/risk-bands/types";
import { downloadRiskBandsCsv } from "@/tools/risk-bands/utils/download";
import { downloadExposureCsv } from "@/tools/risk-bands/utils/downloadExposure";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import { Rcp, rcpValues } from "@/types/rcp";
import { returnPeriods } from "@/types/returnPeriod";
import { concatLatLng } from "@/utils/geocoding";
import { formatSettingSwitchValue } from "@/utils/string";
import BarsOutlined from "@ant-design/icons/es/icons/BarsOutlined";
import GlobalOutlined from "@ant-design/icons/es/icons/GlobalOutlined";
import { Col, ConfigProvider, Row, Segmented, Select, Space } from "antd";
import { SegmentedValue } from "antd/es/segmented";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
type ScenarioOptsType = {
  value: Rcp;
  label: string;
}[];
interface Step3Props {
  results: GeocodeWithResult[];
  unmatched: UnmatchedType[];
  advancedSettings: Partial<RiskBandsAdvancedSettingsValues>;
  setRefs: (ref: RiskBandsBatchStep3Refs) => void;
  onRetry: () => void;
  option: string;
  setOption: (option: string) => void;
  isDownloadResultsModalTutorialOpen: boolean;
}
export interface RiskBandsBatchStep3Refs {
  downloadResults: ScreeningResultModalRefs | undefined;
  downloadResultsTrigger: MutableRefObject<null>;
  map: MutableRefObject<null>;
  rcp: MutableRefObject<null>;
  metrics: MutableRefObject<null>;
  table: MutableRefObject<null>;
  tabToggler: MutableRefObject<null>;
}
export const RiskBandsBatchStep3 = ({
  results,
  unmatched,
  advancedSettings,
  setRefs,
  onRetry,
  option,
  setOption,
  isDownloadResultsModalTutorialOpen
}: Step3Props) => {
  const [response, setResponse] = useState<GeocodeWithSingleRcp[]>();
  const [selectedResultsMetric, setSelectedResultsMetric] = useState<AnalysisResultsTab>("risk");
  const [scenario, setScenario] = useState<Rcp>("rcp85");
  const [showScreeningResultsModal, setShowScreeningResultsModal] = useState<boolean>(false);
  const {
    t
  } = useTranslation();
  const {
    t: terms
  } = useTranslation("terms");
  const theme = getTheme();
  const metricsOptions = [{
    label: terms("simpleRiskRatings"),
    value: "risk"
  }, {
    label: "Flood Exposure",
    value: "exposure"
  }];
  const [getTpuLabelling, {
    data: tpuData
  }] = useLazyGetTpuLabellingQuery();
  const [downloadResultsRef, setDownloadResultsRef] = useState<ScreeningResultModalRefs | undefined>(undefined);
  const downloadResultsTriggerRef = useRef(null);
  const mapRef = useRef(null);
  const rcpRef = useRef(null);
  const tableRef = useRef(null);
  const tabTogglerRef = useRef(null);
  const metricsRef = useRef(null);
  useEffect(() => {
    setRefs({
      downloadResults: downloadResultsRef,
      downloadResultsTrigger: downloadResultsTriggerRef,
      map: mapRef,
      rcp: rcpRef,
      metrics: metricsRef,
      table: tableRef,
      tabToggler: tabTogglerRef
    });
  }, [downloadResultsRef, downloadResultsTriggerRef, mapRef, rcpRef, metricsRef, tableRef, tabTogglerRef]);
  const scenarioOpts: ScenarioOptsType = rcpValues.map(rcp => {
    return {
      value: rcp,
      label: terms(`scenarios.${rcp}.medium`)
    };
  });
  const isWordy = () => {
    if (results.length && results[0].result && results[0].result.mode) {
      return results[0].result.mode !== "abc";
    }
    return false;
  };
  useEffect(() => {
    (async () => {
      await logToolUsage({
        id: "screening.batch",
        type: "quick_batch:simple_risk_bands",
        count: results.length,
        archetype: advancedSettings.archetype
      });
      store.dispatch(supabaseApi.endpoints.getToolUsage.initiate({
        activeQuotaOnly: true
      }, {
        forceRefetch: true
      }));
    })();
  }, []);
  useEffect(() => {
    const res = results.map(asset => {
      const result = asset.result.mode !== "abc" ? asset.result[scenario] : asset.result["rcp85"];
      return {
        geocode: asset.geocode,
        result,
        metadata: asset.result.metadata
      };
    });
    setResponse(res);
  }, [scenario]);
  useEffect(() => {
    if (isTpuLabellingEnabled()) {
      getTpuLabelling(results.reduce((acc, value) => {
        const {
          lat,
          lng
        } = value.geocode.latLng;
        const id = concatLatLng(value.geocode.latLng);
        return {
          ...acc,
          [id]: [lat, lng]
        };
      }, {} as GetTpuSubunitRequest));
    }
  }, [results]);
  const handleChange = (value: SegmentedValue) => setOption(value.valueOf() as string);
  return <Space orientation="vertical" className="w-full" size="middle" data-sentry-element="Space" data-sentry-component="RiskBandsBatchStep3" data-sentry-source-file="BatchStep3.tsx">
      <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
        <div className="mb-2 flex w-full justify-between">
          <ConfigProvider theme={{
          components: {
            Segmented: {
              borderRadius: 999,
              itemSelectedBg: theme.antd.token?.colorPrimary || "#1677ff",
              itemSelectedColor: "#ffffff",
              trackBg: "var(--color-gray-700)",
              itemColor: "var(--color-gray-50)",
              itemHoverColor: "var(--color-gray-50)",
              itemHoverBg: "var(--color-gray-500)",
              motionDurationSlow: "0s",
              motionDurationMid: "0s"
            }
          }
        }} data-sentry-element="ConfigProvider" data-sentry-source-file="BatchStep3.tsx">
            <Segmented ref={tabTogglerRef} onChange={handleChange} options={[{
            value: "map",
            label: t("terms:map"),
            icon: <GlobalOutlined />,
            title: "Map"
          }, {
            value: "list",
            label: t("terms:list"),
            icon: <BarsOutlined />,
            title: "List"
          }]} value={option} data-sentry-element="Segmented" data-sentry-source-file="BatchStep3.tsx" />
          </ConfigProvider>
          {isWordy() && <Space size="middle">
              <Space ref={metricsRef} size="small">
                <ContextualInfoButton id={selectedResultsMetric === "exposure" ? "exposure" : "simpleRiskRatings"} />
                <Select onChange={value => {
              setSelectedResultsMetric(value);
              if (value === "exposure") setScenario("rcp85");
            }} options={metricsOptions} defaultValue={selectedResultsMetric} className="w-[175px] text-left" />
              </Space>
              {selectedResultsMetric === "risk" ? <Space size="small" ref={rcpRef}>
                  <ContextualInfoButton id="emissionScenarios" />
                  <Select onChange={value => setScenario(value)} options={scenarioOpts} defaultValue={scenario} className="w-full max-w-[360px] text-left" value={scenario} />
                </Space> : <Space size="small">
                  <ContextualInfoButton id="returnPeriod" />
                  <Select prefix="Return Period:" defaultValue={screeningFloodExposureReturnPeriod} value={screeningFloodExposureReturnPeriod} className="w-[286px]">
                    {returnPeriods.map(rp => <Select.Option disabled={rp !== screeningFloodExposureReturnPeriod} key={rp} value={rp}>
                        {getReturnPeriodLabel(rp, false)}
                      </Select.Option>)}
                  </Select>
                </Space>}
            </Space>}
        </div>
        {!!response && <>
            {option === "map" && <Col ref={mapRef} span={24} key={scenario} className={`${styles.fadeIn} min-h-[640px]`}>
                <HubMapPointsDisplay className={styles.map} activeTab={selectedResultsMetric} displayReturnPeriod={screeningFloodExposureReturnPeriod} limitReturnPeriods={[screeningFloodExposureReturnPeriod]} points={response.map(({
            geocode,
            result,
            metadata
          }) => ({
            name: geocode.name,
            input: geocode.input,
            latLng: geocode.latLng,
            response: result,
            quality: geocode.quality,
            metadata,
            hk: tpuData?.[concatLatLng(geocode.latLng)]
          }))} />
              </Col>}

            {option === "list" && <Col span={24} key={scenario} className={styles.fadeIn} ref={tableRef}>
                <MultipleResultsTable aria-label="Results Table" activeTab={selectedResultsMetric} data={response.map(data => ({
            ...data,
            hk: tpuData?.[concatLatLng(data.geocode.latLng)]
          }))} />
              </Col>}
            <div className="mt-4 flex justify-end">
              <span ref={downloadResultsTriggerRef}>
                <DownloadButton type="primary" onClick={() => setShowScreeningResultsModal(true)}>
                  Download Results
                </DownloadButton>
              </span>
              <ScreeningResultsModal tool="Quick Batch Screen" setRefs={setDownloadResultsRef} isOpen={isDownloadResultsModalTutorialOpen || showScreeningResultsModal} onRiskDownload={() => downloadRiskBandsCsv({
            advancedSettings,
            filename: "quick-batch - risk-bands.csv",
            results,
            tpuData
          })} onExposureDownload={() => downloadExposureCsv({
            advancedSettings,
            filename: "quick-batch - flood-exposure.csv",
            results,
            tpuData
          })} onClose={() => setShowScreeningResultsModal(false)} />
            </div>
            {unmatched.length > 0 && <Col span={24} className="mt-4">
                <UnmatchedAddressesCollapse addresses={unmatched} onRetry={onRetry} />
              </Col>}
          </>}
      </div>

      <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
        <div className="max-w-[600px]">
          <Row gutter={[0, 4]} data-sentry-element="Row" data-sentry-source-file="BatchStep3.tsx">
            <Col span={24} data-sentry-element="Col" data-sentry-source-file="BatchStep3.tsx">
              <div className="mb-2 mt-4 font-semibold">{t("tools:settings.advancedAnalysisSettings")}</div>
            </Col>
            <Col span={12} data-sentry-element="Col" data-sentry-source-file="BatchStep3.tsx">
              <ContextualInfoButtonWithText rootClassName="text-gray-400" id="anomalyManagementRp" data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="BatchStep3.tsx">
                {t("tools:settings.anomalyManagementRp.title")}
              </ContextualInfoButtonWithText>
            </Col>
            <Col span={12} data-sentry-element="Col" data-sentry-source-file="BatchStep3.tsx">
              <span className="text-sm">{formatSettingSwitchValue(advancedSettings.anomalyManagementRp)}</span>
            </Col>
            <Col span={12} data-sentry-element="Col" data-sentry-source-file="BatchStep3.tsx">
              <ContextualInfoButtonWithText rootClassName="text-gray-400" id="defences" data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="BatchStep3.tsx">
                {t("tools:settings.defences.title")}
              </ContextualInfoButtonWithText>
            </Col>
            <Col span={12} data-sentry-element="Col" data-sentry-source-file="BatchStep3.tsx">
              <span className="text-sm">{formatSettingSwitchValue(advancedSettings.defences)}</span>
            </Col>
          </Row>
        </div>
      </div>
    </Space>;
};